import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { MediumButton, MediumPrimaryButton } from '../../../components/Buttons';
import { ModalContainer, ModalBody, ModalHeader, ModalFooter } from '../../../components/ModalElements';
import { Label, FormFieldContainer, FormField, Select } from '../../../components/FormElements';
import useSessionTimeline from '../hooks/useSessionTimeline';
import { Segment } from '../models/timeline';
import { Breakpoints } from '../../../app/StyleGuide';

const AddSegmentModalContainer = styled(ModalContainer)`
  form {
    @media screen and (max-width: ${Breakpoints.screen_xs}) {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
  }
`;

const AddSegmentModalBody = styled(ModalBody)`
  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    max-height: none;
    overflow-y: visible;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    max-height: none;
    overflow-y: visible;
  }
`;

const AddSegmentModal = ({
  segment,
  defaultGroup,
  defaultLeader,
  isFlexOption,
  onSubmit,
  handleDismiss,
}: {
  segment?: Segment;
  defaultGroup?: string;
  defaultLeader?: string;
  isFlexOption?: boolean;
  onSubmit: (values: Segment) => void;
  handleDismiss: () => void;
}) => {
  const { segmentGroupMap, segmentLeaderMap, segmentActivityMap } = useSessionTimeline();

  const isEdit = !!segment?.segmentId;
  const isFlexSegment = !!segment?.isFlex;

  const shouldShowFlexActivity = (isEdit && isFlexSegment) || (!isEdit && !isFlexOption);

  const entityType = isFlexOption ? 'Flex Option' : 'Segment';
  const ModalHeaderText = `${isEdit ? 'Edit' : 'Add'} ${entityType}`;
  const ConfirmationButtonText = `${isEdit ? 'Save' : 'Add'} ${entityType}`;

  const submit = (values: Segment) => {
    if (isFlexSegment) delete values.activity;

    onSubmit(values);
  };

  const getFirstKey = (obj: any) => Object.keys(obj)[0];

  const getInitialValues = (): Segment => {
    if (isEdit) {
      if (isFlexSegment) return { ...segment, activity: 'Flex' };
      return segment;
    }

    const initialValues: Segment = {
      name: '',
      group: defaultGroup || getFirstKey(segmentGroupMap),
      leader: defaultLeader || getFirstKey(segmentLeaderMap),
      duration: 5,
      activity: getFirstKey(segmentActivityMap) as Segment['activity'],
      enabled: true,
      isFlex: false,
      sections: [],
      segmentId: '',
      tags: [],
    };

    if (isFlexOption) return { ...initialValues, ...segment };
    return initialValues;
  };

  return (
    <AddSegmentModalContainer dismissHandler={handleDismiss}>
      <ModalHeader data-qa-hook="addEditSegmentModal">
        <h3>{ModalHeaderText}</h3>
      </ModalHeader>
      <Formik
        initialValues={getInitialValues()}
        enableReinitialize={true}
        validationSchema={Yup.object({
          name: Yup.string().max(64, 'Name is too long').required('A name is required'),
          duration: Yup.number().min(0, 'Duration can not be negative'),
        })}
        onSubmit={submit}
      >
        {({ dirty, values, isSubmitting, handleChange }) => (
          <Form id="add-session-form">
            <AddSegmentModalBody>
              <div className="grid-container grid-col-4 grid-col-gap-24">
                <div className="grid-col-span-4">
                  <FormField
                    data-qa-hook="addSegmentName"
                    label="Segment Name"
                    name="name"
                    type="text"
                    placeholder="Enter Segment Name"
                  />
                </div>
                <div className="grid-sm-col-span-2 grid-col-span-4">
                  <FormFieldContainer>
                    <Label htmlFor="group">Group</Label>
                    <Select
                      value={values.group}
                      id="group"
                      name="group"
                      aria-label="Select Group"
                      onChange={handleChange}
                      disabled={isFlexOption}
                    >
                      {Object.entries(segmentGroupMap).map(([key, displayName]) => (
                        <option key={key} value={key}>
                          {displayName}
                        </option>
                      ))}
                    </Select>
                  </FormFieldContainer>
                </div>
                <div className="grid-sm-col-span-2 grid-col-span-4">
                  <FormFieldContainer>
                    <Label htmlFor="leader">Leader</Label>
                    <Select
                      value={values.leader}
                      id="leader"
                      name="leader"
                      aria-label="Select Leader"
                      onChange={handleChange}
                      disabled={isFlexOption}
                    >
                      {Object.entries(segmentLeaderMap).map(([key, displayName]) => (
                        <option key={key} value={key}>
                          {displayName}
                        </option>
                      ))}
                    </Select>
                  </FormFieldContainer>
                </div>
                <div className="grid-sm-col-span-2 grid-col-span-4">
                  <FormField
                    data-qa-hook="addDuration"
                    label="Duration (minutes)"
                    name="duration"
                    type="number"
                    placeholder="Enter Duration Minutes"
                    min="0"
                    disabled={isFlexOption}
                  />
                </div>
                <div className="grid-sm-col-span-2 grid-col-span-4">
                  <FormFieldContainer>
                    <Label htmlFor="activity">Type</Label>
                    <Select
                      value={values.activity}
                      id="activity"
                      name="activity"
                      aria-label="Select Activity"
                      onChange={handleChange}
                      disabled={isFlexSegment}
                    >
                      {Object.entries(segmentActivityMap).map(([key, displayName]) => (
                        <option key={key} value={key}>
                          {displayName}
                        </option>
                      ))}
                      {shouldShowFlexActivity && (
                        <option key="Flex" value="Flex">
                          Flex
                        </option>
                      )}
                    </Select>
                  </FormFieldContainer>
                </div>
              </div>
            </AddSegmentModalBody>
            <ModalFooter>
              {isEdit && !dirty ? (
                <MediumPrimaryButton data-qa-hook="addSegmentConfirm" onClick={onSubmit}>
                  {ConfirmationButtonText}
                </MediumPrimaryButton>
              ) : (
                <MediumPrimaryButton
                  data-qa-hook="addSegmentConfirm"
                  type="submit"
                  disabled={isSubmitting}
                  operating={isSubmitting}
                >
                  {ConfirmationButtonText}
                </MediumPrimaryButton>
              )}
              <MediumButton data-qa-hook="addSegmentCancel" onClick={handleDismiss}>
                Cancel
              </MediumButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </AddSegmentModalContainer>
  );
};

export default AddSegmentModal;
